import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { KpiService } from "../../../services/shipment/kpi.ts";
import {
  KpiShipmentPerformanceFilter,
  KpiShipmentPerformance,
} from "../../../Models/KpiShipmentPerformance.ts";

const kpiService = new KpiService();

interface ShipmentsPerformancesFirstAttemptDeliveredProviderProps {
  children: ReactNode;
  filters: KpiShipmentPerformanceFilter;
}

interface ShipmentsPerformancesFirstAttemptDeliveredContextValue {
  data: KpiShipmentPerformance[];
  loader: boolean;
  filters: KpiShipmentPerformanceFilter;
}

const ShipmentsPerformancesFirstAttemptDeliveredContext = createContext<
  ShipmentsPerformancesFirstAttemptDeliveredContextValue | undefined
>(undefined);

const ShipmentsPerformancesFirstAttemptDeliveredProvider: React.FC<
  ShipmentsPerformancesFirstAttemptDeliveredProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentPerformance[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentPerformance> | null>(null);

  const prevFilters = useRef<KpiShipmentPerformanceFilter>(filters);

  const getPerformancesFirstAttemptDelivered = (): void => {
    setLoader(true);
    kpiService
      .getPerformancesFirstAttemptDelivered(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getPerformancesFirstAttemptDelivered();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getPerformancesFirstAttemptDelivered();
  }, []);

  return (
    <ShipmentsPerformancesFirstAttemptDeliveredContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsPerformancesFirstAttemptDeliveredContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsPerformancesFirstAttemptDeliveredContext.
 * Ensures that the context is used only within a ShipmentsPerformancesFirstAttemptDeliveredProvider.
 */
const useShipmentsPerformancesFirstAttemptDeliveredContext =
  (): ShipmentsPerformancesFirstAttemptDeliveredContextValue => {
    const context = useContext(
      ShipmentsPerformancesFirstAttemptDeliveredContext
    );
    if (!context) {
      throw new Error(
        "useShipmentsPerformancesFirstAttemptDeliveredContext must be used within a ShipmentsPerformancesFirstAttemptDeliveredProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export {
  ShipmentsPerformancesFirstAttemptDeliveredProvider,
  useShipmentsPerformancesFirstAttemptDeliveredContext,
};
